import React, { useEffect, useState, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames'
import Select from 'react-select'
import Table, { type TableColumnConfig } from '../../../../components/Table/Table'
import Badge from '../../../../components/Badge'
import Button from '../../../../components/Form/Button'
import { formatUTCDate, formatCurrency } from '../../../../util/string'
import { TenantAccountBillingEventEntry, EditBillingEventRequest, ReverseInvoiceAccruedRequest, BillingEventEnums, BillingEventAuditLogEntry, UpdateAuditLogEventBodyRequest } from '../../../../apis/types'
import {
  getBillingEventsApi,
  reclassBillingEventApi,
  getReversalStatusApi,
  editBillingEventApi,
  getBillingEventEnumsApi,
  reversePaymentApi,
  reverseInvoiceAccruedApi,
  getBillingEventAuditLogApi,
  updateAuditLogStatusApi,
  updateAuditLogEventBodyApi,
  exportBillingEventsCsvApi,
} from '../../../../apis/apis'
import { createCustomSelectStyles } from '../../../users/components/utils/select-styles'
import { useAppSelector, useAppDispatch } from '../../../../shared/redux/hooks'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { getInputStyles } from '../../../../components/Form/styles'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import { useTabHeaderActions } from './TabPanel'

function formatBillingCurrency(value: number | null): React.ReactNode {
  if (value === null || isNaN(Number(value))) {
    return <span className="text-slate-400 dark:text-slate-500">—</span>
  }
  return (
    <span className={classnames('tabular-nums', value < 0 && 'text-red-600 dark:text-red-400')}>{formatCurrency(value)}</span>
  )
}

function formatBillingDate(value: string | null | undefined): string {
  if (value == null) return ''
  return formatUTCDate(new Date(value))
}

/** Compact enum chip for billing event tables (matches account-header soft badges). */
function EnumBadge({ status, className }: { status?: string | null; className?: string }): React.ReactElement | null {
  if (status == null || status === '' || status === 'N/A') {
    return null
  }
  return <Badge status={status} variant="soft" className={className} truncate={false} />
}

function TruncatedCellWithTooltip({
  text,
  maxWidthClass,
}: {
  text: string
  maxWidthClass: string
}): React.ReactElement | null {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })

  if (!text) {
    return null
  }

  const showTooltip = (): void => {
    const el = anchorRef.current
    if (el == null || el.scrollWidth <= el.clientWidth) {
      return
    }
    const rect = el.getBoundingClientRect()
    setCoords({ top: rect.bottom + 4, left: rect.left })
    setVisible(true)
  }

  return (
    <>
      <span
        ref={anchorRef}
        className={classnames(maxWidthClass, 'truncate block')}
        onMouseEnter={showTooltip}
        onMouseLeave={() => setVisible(false)}
      >
        {text}
      </span>
      {visible &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[9999] max-w-sm rounded-md bg-slate-600 px-2 py-1 text-xs font-normal normal-case text-white shadow-lg dark:bg-slate-950"
            style={{ top: coords.top, left: coords.left }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  )
}

const CHARGES_COLUMN_GROUP_ID = 'charges'

function hasNonZeroNumericValue(
  rows: TenantAccountBillingEventEntry[],
  getValue: (row: TenantAccountBillingEventEntry) => number | null
): boolean {
  return rows.some((row) => {
    const value = getValue(row)
    return value != null && !isNaN(Number(value)) && Number(value) !== 0
  })
}

export interface TabBillingEventsProps {
  title: string
  tenantAccountId: string
}

interface EventTypeOption {
  value: string
  label: string
}

const parseEntryId = (entryId: string): { main: number; sub: number } => {
  const parts = entryId.split('-')
  if (parts.length === 2) {
    const mainPart = parseInt(parts[0], 10)
    const subPart = parseInt(parts[1], 10)
    return { main: isNaN(mainPart) ? 0 : mainPart, sub: isNaN(subPart) ? 0 : subPart }
  }
  const parsed = parseInt(entryId, 10)
  return { main: isNaN(parsed) ? 0 : parsed, sub: 0 }
}

const sortBillingEvents = (a: TenantAccountBillingEventEntry, b: TenantAccountBillingEventEntry): number => {
  const eventIdA = parseInt(a.eventId)
  const eventIdB = parseInt(b.eventId)
  if (eventIdA !== eventIdB) {
    return eventIdB - eventIdA
  }
  const entryA = parseEntryId(a.entryId)
  const entryB = parseEntryId(b.entryId)
  if (entryA.main !== entryB.main) {
    return entryB.main - entryA.main
  }
  return entryB.sub - entryA.sub
}

export const TabBillingEvents: React.FC<TabBillingEventsProps> = (props) => {
  const [data, setData] = useState<TenantAccountBillingEventEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null)
  const [dropdownCoords, setDropdownCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Audit log state
  const [auditLogData, setAuditLogData] = useState<BillingEventAuditLogEntry[]>([])
  const [auditLogLoading, setAuditLogLoading] = useState(false)
  const [auditLogError, setAuditLogError] = useState<string | null>(null)
  const [activeAuditLogDropdownId, setActiveAuditLogDropdownId] = useState<string | null>(null)
  const [auditLogDropdownCoords, setAuditLogDropdownCoords] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const auditLogDropdownRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Audit log modal state
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [isEventBodyModalOpen, setIsEventBodyModalOpen] = useState(false)
  const [selectedAuditLog, setSelectedAuditLog] = useState<BillingEventAuditLogEntry | null>(null)
  const [auditLogStatusForm, setAuditLogStatusForm] = useState<{ status: 'QUEUED' | 'SKIPPED'; modifiedDescription: string }>({
    status: 'QUEUED',
    modifiedDescription: '',
  })
  const [auditLogEventBodyForm, setAuditLogEventBodyForm] = useState<UpdateAuditLogEventBodyRequest>({
    eventType: '',
    transactionDate: '',
    sourceEntity: '',
    amount: undefined,
    skipChecks: false,
    eventSubType: '',
    transactionStartDate: '',
    modifiedDescription: '',
  })
  const [selectedAuditLogTenantCuid, setSelectedAuditLogTenantCuid] = useState<string>('')
  const [isUpdatingAuditLog, setIsUpdatingAuditLog] = useState(false)

  // Modal state for Reverse/Reclass
  const [isReverseModalOpen, setIsReverseModalOpen] = useState(false)
  const [isReclassModalOpen, setIsReclassModalOpen] = useState(false)
  const [selectedEventForModal, setSelectedEventForModal] = useState<TenantAccountBillingEventEntry | null>(null)
  const [customTransactionDate, setCustomTransactionDate] = useState('')
  const [useOriginalDate, setUseOriginalDate] = useState(true)

  // Modal state for Reverse Invoice
  const [isReverseInvoiceModalOpen, setIsReverseInvoiceModalOpen] = useState(false)
  const [isReversingInvoice, setIsReversingInvoice] = useState(false)
  const [reverseInvoiceStep, setReverseInvoiceStep] = useState<'form' | 'confirm'>('form')
  const [reverseInvoiceFormData, setReverseInvoiceFormData] = useState<ReverseInvoiceAccruedRequest>({
    amount: undefined,
    eventSubType: undefined,
    modifiedDescription: '',
  })
  const [reverseInvoiceUseOriginalAmount, setReverseInvoiceUseOriginalAmount] = useState(true)
  const [reverseInvoiceAmountError, setReverseInvoiceAmountError] = useState<string | null>(null)
  const { permissions } = useAppSelector((state) => state.app)
  const dispatch = useAppDispatch()
  const [selectedEventTypes, setSelectedEventTypes] = useState<EventTypeOption[]>([])
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isReclassifying, setIsReclassifying] = useState(false)
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [collapsedColumnGroups, setCollapsedColumnGroups] = useState<Set<string>>(
    () => new Set([CHARGES_COLUMN_GROUP_ID])
  )

  const toggleColumnGroup = (groupId: string): void => {
    setCollapsedColumnGroups((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  // Edit mode state
  const [editMode, setEditMode] = useState<string | null>(null) // eventId being edited
  const [editFormData, setEditFormData] = useState<EditBillingEventRequest>({
    eventType: '',
    eventSubType: '',
    postingDate: '',
    transactionDate: '',
    amount: 0,
    // BillingEntry fields
    corpEnergyCharge: 0,
    corpDiscount: 0,
    utilityChargeTenantToCorp: 0,
    utilityChargeCorpToUtility: 0,
    unbilledPayableTenantToCorp: 0,
    payableTenantToCorp: 0,
    payableCorpToUtility: 0,
    corpNsfCharge: 0,
    corpLateFeeCharge: 0,
    corpProcessingCharge: 0,
    miscCharge: 0,
    dueTenantToCorp: 0,
    dueCorpToUtility: 0,
    modifiedDescription: '',
  })
  const [isEditing, setIsEditing] = useState(false)
  const [showEditConfirmation, setShowEditConfirmation] = useState(false)

  // Store enum values fetched from API
  const [billingEventEnums, setBillingEventEnums] = useState<BillingEventEnums | null>(null)
  const [enumsLoading, setEnumsLoading] = useState(false)

  const selectStyles = useMemo(() => {
    const baseStyles = createCustomSelectStyles<EventTypeOption>()
    return {
      ...baseStyles,
      input: (styles: any) => ({
        ...styles,
        input: {
          ...styles.input,
          color: 'rgb(107 114 128) !important',
          outline: 'none !important',
          boxShadow: 'none !important',
          ':focus': {
            outline: 'none !important',
            boxShadow: 'none !important',
            '--tw-ring-color': 'transparent !important',
          },
        },
      }),
      menuPortal: (styles: Record<string, unknown>) => ({ ...styles, zIndex: 200 }),
    }
  }, [])

  // Extract distinct event types for filter options
  const eventTypeOptions = useMemo(() => {
    const uniqueTypes = [...new Set(data.map((item) => item.eventType))].filter((type) => type && type.trim() !== '').sort()
    return uniqueTypes.map((type) => ({ value: type, label: type }))
  }, [data])

  // Filter data based on selected event types
  const filteredData = useMemo(() => {
    if (selectedEventTypes.length === 0) {
      return data
    }
    const selectedValues = selectedEventTypes.map((option) => option.value)
    return data.filter((item) => selectedValues.includes(item.eventType))
  }, [data, selectedEventTypes])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false)
      }
    }

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isFilterOpen])

  // Check if user has payment permission (same as payment posting)
  const hasTakePaymentPermission = (): boolean => (permissions != null ? permissions.includes('payops:take:payment') : false)

  // Check if user has billing event edit permission
  const hasEditBillingEventPermission = (): boolean => (permissions != null ? permissions.includes('billing:edit:billing-event') : false)

  // Check if user has billing event create permission
  const hasCreateBillingEventPermission = (): boolean => (permissions != null ? permissions.includes('billing:create:billing-event') : false)

  // Check if event type can be invoice-reversed
  const canReverseInvoice = (eventType: string): boolean => {
    return hasCreateBillingEventPermission() && eventType === 'INVOICE_ACCRUED'
  }

  // Track events that have been reversed (from API response)
  const [reversalStatus, setReversalStatus] = useState<{ [eventId: string]: boolean }>({})

  // Check if an event has been reversed
  const hasBeenReversed = (eventId: string): boolean => {
    return reversalStatus[eventId] === true
  }

  // Check if event type can be reclassified
  const canReclass = (eventType: string): boolean => {
    const hasPermission = hasTakePaymentPermission()
    const isReclassableType = eventType === 'TENANT_UTILITY_PAYMENT' || eventType === 'CORP_UTILITY_PAYMENT'
    return hasPermission && isReclassableType
  }

  // Check if event type can be reversed
  const canReverse = (eventType: string): boolean => {
    const hasPermission = hasTakePaymentPermission()
    const isReversibleType = eventType === 'TENANT_UTILITY_PAYMENT' || eventType === 'CORP_UTILITY_PAYMENT'
    return hasPermission && isReversibleType
  }

  // Get reclass target for an event type
  const getReclassTarget = (eventType: string): string | null => {
    if (eventType === 'TENANT_UTILITY_PAYMENT') return 'CORP_UTILITY_PAYMENT'
    if (eventType === 'CORP_UTILITY_PAYMENT') return 'TENANT_UTILITY_PAYMENT'
    return null
  }

  // Handle CSV download
  const handleDownloadCsv = async () => {
    try {
      setIsDownloadingCsv(true)
      const blob = await exportBillingEventsCsvApi(props.tenantAccountId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      // Build descriptive filename: BillingEvents_AccountName_YYYYMMDD_HHMMSS.csv
      const accountName = data[0]?.accountName?.replace(/[^a-zA-Z0-9]/g, '_') || 'Unknown'
      const timestamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 15)
      link.download = `BillingEvents_${accountName}_${timestamp}.csv`

      link.click()
      window.URL.revokeObjectURL(url)

      dispatch(
        pushToast({
          message: 'CSV downloaded successfully',
        })
      )
    } catch (err) {
      console.error('Error downloading CSV:', err)
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to download CSV',
          description: err instanceof Error ? err.message : 'Unknown error occurred',
        })
      )
    } finally {
      setIsDownloadingCsv(false)
    }
  }

  const downloadCsvRef = useRef(handleDownloadCsv)
  downloadCsvRef.current = handleDownloadCsv

  const csvHeaderAction = useMemo(
    () => (
      <Button
        onClick={() => void downloadCsvRef.current()}
        disabled={isDownloadingCsv}
        isSaving={isDownloadingCsv}
        size={2}
        color="slate"
      >
        <ArrowDownTrayIcon className="w-4 h-4 mr-1.5 inline-block" />
        Download CSV
      </Button>
    ),
    [isDownloadingCsv]
  )
  useTabHeaderActions(csvHeaderAction, data.length > 0 && !loading && error == null)

  // Handle reverse action
  const handleReverse = async (eventId: string, transactionDate?: string) => {
    try {
      setIsReclassifying(true)

      const result = await reversePaymentApi(props.tenantAccountId, eventId, transactionDate)

      if (result.success) {
        dispatch(
          pushToast({
            message: 'Payment reversed successfully',
            description: result.message,
          })
        )

        // Refresh both billing events data and reversal status
        const refreshedData = await getBillingEventsApi(props.tenantAccountId)
        let refreshedReversalStatus: { [eventId: string]: boolean } = {}

        if (hasTakePaymentPermission()) {
          refreshedReversalStatus = await getReversalStatusApi(props.tenantAccountId)
        }

        setData(refreshedData.sort(sortBillingEvents))
        setReversalStatus(refreshedReversalStatus)
      }
    } catch (error: any) {
      console.error('Failed to reverse payment:', error)

      // Check if this is an "already reversed" error
      const errorMessage = error.response?.data?.message ?? error.response?.message ?? error.message ?? ''
      if (
        errorMessage.includes('has already been reversed') ||
        errorMessage.includes('has a pending reversal') ||
        errorMessage.includes('already been reversed') ||
        errorMessage.includes('pending reversal')
      ) {
        // Mark this event as reversed so UI shows crossed-out state
        setReversalStatus((prev) => ({ ...prev, [eventId]: true }))
      }

      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to reverse payment',
          description: errorMessage,
        })
      )
    } finally {
      setIsReclassifying(false)
      setActiveDropdownId(null)
      setUseOriginalDate(true)
      setCustomTransactionDate('')
    }
  }

  const resetReverseInvoiceForm = () => {
    setReverseInvoiceFormData({ amount: undefined, eventSubType: undefined, modifiedDescription: '' })
    setReverseInvoiceUseOriginalAmount(true)
    setReverseInvoiceAmountError(null)
    setReverseInvoiceStep('form')
  }

  const closeReverseInvoiceModal = () => {
    setIsReverseInvoiceModalOpen(false)
    setSelectedEventForModal(null)
    resetReverseInvoiceForm()
  }

  // Validate optional custom reversal amount client-side so the operator gets immediate
  // feedback instead of waiting on a 400.
  const validateReverseInvoiceAmount = (event: TenantAccountBillingEventEntry, amount: number | undefined): string | null => {
    if (amount === undefined || isNaN(amount)) {
      return 'Enter an amount'
    }
    if (amount <= 0) {
      return 'Reversal amount must be greater than zero'
    }
    if (amount > Math.abs(event.amount)) {
      return 'Amount cannot exceed original invoice amount'
    }
    return null
  }

  const buildReverseInvoicePayload = (): ReverseInvoiceAccruedRequest => ({
    modifiedDescription: reverseInvoiceFormData.modifiedDescription,
    amount: reverseInvoiceUseOriginalAmount ? undefined : reverseInvoiceFormData.amount,
    eventSubType: reverseInvoiceFormData.eventSubType || undefined,
  })

  // Classify the reverse-invoice 400 messages documented by the backend so the UI can react
  // appropriately (refresh reversal-status, keep the modal open, etc.) instead of treating
  // every error the same way.
  type ReverseInvoiceErrorClass = 'alreadyReversed' | 'pendingReversal' | 'other'

  const classifyReverseInvoiceError = (message: string): ReverseInvoiceErrorClass => {
    if (!message) return 'other'
    if (message.includes('has already been reversed')) return 'alreadyReversed'
    // Covers both the "pending reversal (audit log ID: ...)" case and the concurrent
    // duplicate-click case, which has the same "has a pending reversal" text but no ID.
    if (message.includes('has a pending reversal')) return 'pendingReversal'
    return 'other'
  }

  // Handle reverse invoice action
  const handleReverseInvoice = async (eventId: string, payload: ReverseInvoiceAccruedRequest) => {
    try {
      setIsReversingInvoice(true)

      const result = await reverseInvoiceAccruedApi(props.tenantAccountId, eventId, payload)

      if (result.success) {
        closeReverseInvoiceModal()

        dispatch(
          pushToast({
            message: 'Invoice reversal queued',
            description: result.message || 'The reversal will post automatically on the next billing-event processor run.',
          })
        )

        // Refresh billing events, reversal status, and audit log independently so a
        // failure in one does not discard already-fetched data from the others
        try {
          const refreshedData = await getBillingEventsApi(props.tenantAccountId)
          setData(refreshedData.sort(sortBillingEvents))
        } catch (refreshError) {
          console.error('Failed to refresh billing events after invoice reversal:', refreshError)
        }

        try {
          if (hasTakePaymentPermission() || hasCreateBillingEventPermission()) {
            const refreshedReversalStatus = await getReversalStatusApi(props.tenantAccountId)
            setReversalStatus(refreshedReversalStatus)
          }
        } catch (refreshError) {
          console.error('Failed to refresh reversal status after invoice reversal:', refreshError)
        }

        try {
          const refreshedAuditLog = await getBillingEventAuditLogApi(props.tenantAccountId)
          setAuditLogData(refreshedAuditLog)
        } catch (refreshError) {
          console.error('Failed to refresh audit log after invoice reversal:', refreshError)
        }
      }
    } catch (error: any) {
      console.error('Failed to reverse invoice:', error)

      const errorMessage: string = error.response?.data?.message ?? error.response?.message ?? error.message ?? ''
      const errorClass = classifyReverseInvoiceError(errorMessage)

      if (errorClass === 'alreadyReversed' || errorClass === 'pendingReversal') {
        // Treat "already reversed" and both "pending reversal" variants (with or without an
        // audit log ID, e.g. the concurrent duplicate-click case) the same: another reversal
        // already exists for this event. Optimistically lock the row, then refresh from the
        // server so the UI reflects the authoritative state.
        setReversalStatus((prev) => ({ ...prev, [eventId]: true }))

        try {
          if (hasTakePaymentPermission() || hasCreateBillingEventPermission()) {
            const refreshedReversalStatus = await getReversalStatusApi(props.tenantAccountId)
            setReversalStatus(refreshedReversalStatus)
          }
        } catch (refreshError) {
          console.error('Failed to refresh reversal status after reversal conflict:', refreshError)
        }

        dispatch(
          pushToast({
            type: 'error',
            message: 'Reversal already in progress',
            description: 'Another reversal for this invoice has already been queued or posted. Refresh to see the latest status.',
          })
        )
      } else {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Failed to reverse invoice',
            description: errorMessage,
          })
        )
      }
    } finally {
      setIsReversingInvoice(false)
      setActiveDropdownId(null)
    }
  }

  // Handle reclass action
  const handleReclass = async (eventId: string, _currentType: string, newType: string, transactionDate?: string) => {
    try {
      setIsReclassifying(true)

      const result = await reclassBillingEventApi(props.tenantAccountId, eventId, newType, transactionDate)

      if (result.success) {
        dispatch(
          pushToast({
            message: 'Event reclassified successfully',
            description: result.message,
          })
        )

        // Refresh both billing events data and reversal status
        const refreshedData = await getBillingEventsApi(props.tenantAccountId)
        let refreshedReversalStatus: { [eventId: string]: boolean } = {}

        if (hasTakePaymentPermission()) {
          refreshedReversalStatus = await getReversalStatusApi(props.tenantAccountId)
        }

        setData(refreshedData.sort(sortBillingEvents))
        setReversalStatus(refreshedReversalStatus)
      }
    } catch (error: any) {
      console.error('Failed to reclass event:', error)

      // Check if this is an "already reversed" error
      const errorMessage = error.response?.data?.message ?? error.response?.message ?? error.message ?? ''
      if (
        errorMessage.includes('has already been reversed') ||
        errorMessage.includes('has a pending reversal') ||
        errorMessage.includes('already been reversed') ||
        errorMessage.includes('pending reversal')
      ) {
        // Mark this event as reversed so UI shows crossed-out state
        setReversalStatus((prev) => ({ ...prev, [eventId]: true }))
      }

      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to reclassify event',
          description: errorMessage,
        })
      )
    } finally {
      setIsReclassifying(false)
      setActiveDropdownId(null)
      setUseOriginalDate(true)
      setCustomTransactionDate('')
    }
  }

  // Edit mode functions
  const startEdit = (row: TenantAccountBillingEventEntry) => {
    setEditMode(row.eventId)
    setEditFormData({
      eventType: row.eventType,
      eventSubType: row.eventSubtype,
      postingDate: new Date(row.postDate).toISOString().split('T')[0],
      transactionDate: new Date(row.txDate).toISOString().split('T')[0],
      amount: row.amount,
      // BillingEntry fields
      corpEnergyCharge: row.grossKECharge,
      corpDiscount: row.keDiscount,
      utilityChargeTenantToCorp: row.utilityChargeTenantToCorp,
      utilityChargeCorpToUtility: row.utilityChargeCorpToUtility,
      unbilledPayableTenantToCorp: row.unbilledPayableTenantToCorp,
      payableTenantToCorp: row.billedPayableTenantToCorp,
      payableCorpToUtility: row.billedPayableCorpToUtility,
      corpNsfCharge: row.corpNsfCharge,
      corpLateFeeCharge: row.corpLateFeeCharge,
      corpProcessingCharge: row.corpProcessingCharge,
      miscCharge: row.miscCharge,
      dueTenantToCorp: row.duePayableTenantToCorp,
      dueCorpToUtility: row.duePayableCorpToUtility,
      modifiedDescription: '',
    })
    setActiveDropdownId(null) // Close any open dropdowns
  }

  const cancelEdit = () => {
    setEditMode(null)
    setEditFormData({
      eventType: '',
      eventSubType: '',
      postingDate: '',
      transactionDate: '',
      amount: 0,
      // BillingEntry fields
      corpEnergyCharge: 0,
      corpDiscount: 0,
      utilityChargeTenantToCorp: 0,
      utilityChargeCorpToUtility: 0,
      unbilledPayableTenantToCorp: 0,
      payableTenantToCorp: 0,
      payableCorpToUtility: 0,
      corpNsfCharge: 0,
      corpLateFeeCharge: 0,
      corpProcessingCharge: 0,
      miscCharge: 0,
      dueTenantToCorp: 0,
      dueCorpToUtility: 0,
      modifiedDescription: '',
    })
    setShowEditConfirmation(false)
  }

  const handleEditSubmit = () => {
    // Validate all required fields
    const requiredFields = [
      { field: 'eventType', label: 'Event Type' },
      { field: 'eventSubType', label: 'Event Subtype' },
      { field: 'postingDate', label: 'Posting Date' },
      { field: 'transactionDate', label: 'Transaction Date' },
      { field: 'amount', label: 'Amount' },
      { field: 'modifiedDescription', label: 'Description of Changes' },
    ]

    const missingFields = requiredFields.filter(({ field }) => {
      const value = editFormData[field as keyof typeof editFormData]
      if (field === 'amount') {
        return value === undefined || value === null
      }
      return !value || (typeof value === 'string' && !value.trim())
    })

    if (missingFields.length > 0) {
      dispatch(
        pushToast({
          type: 'error',
          message: 'Required fields missing',
          description: `Please fill in: ${missingFields.map((f) => f.label).join(', ')}`,
        })
      )
      return
    }

    setShowEditConfirmation(true)
  }

  const confirmEdit = async () => {
    if (!editMode) return

    try {
      setIsEditing(true)

      // Convert date strings to ISO 8601 format for the API
      const formattedEditData: EditBillingEventRequest = {
        eventType: editFormData.eventType,
        eventSubType: editFormData.eventSubType,
        postingDate: new Date(editFormData.postingDate + 'T00:00:00.000Z').toISOString(),
        transactionDate: new Date(editFormData.transactionDate + 'T00:00:00.000Z').toISOString(),
        amount: editFormData.amount,
        // Include all BillingEntry fields
        corpEnergyCharge: editFormData.corpEnergyCharge,
        corpDiscount: editFormData.corpDiscount,
        utilityChargeTenantToCorp: editFormData.utilityChargeTenantToCorp,
        utilityChargeCorpToUtility: editFormData.utilityChargeCorpToUtility,
        unbilledPayableTenantToCorp: editFormData.unbilledPayableTenantToCorp,
        payableTenantToCorp: editFormData.payableTenantToCorp,
        payableCorpToUtility: editFormData.payableCorpToUtility,
        corpNsfCharge: editFormData.corpNsfCharge,
        corpLateFeeCharge: editFormData.corpLateFeeCharge,
        corpProcessingCharge: editFormData.corpProcessingCharge,
        miscCharge: editFormData.miscCharge,
        dueTenantToCorp: editFormData.dueTenantToCorp,
        dueCorpToUtility: editFormData.dueCorpToUtility,
        modifiedDescription: editFormData.modifiedDescription,
      }

      const result = await editBillingEventApi(props.tenantAccountId, editMode, formattedEditData)

      if (result.success) {
        dispatch(
          pushToast({
            message: 'Event edited successfully',
            description: `Event ${editMode} has been updated.`,
          })
        )

        // Refresh billing events data
        const refreshedData = await getBillingEventsApi(props.tenantAccountId)
        let refreshedReversalStatus: { [eventId: string]: boolean } = {}

        if (hasTakePaymentPermission() || hasEditBillingEventPermission()) {
          refreshedReversalStatus = await getReversalStatusApi(props.tenantAccountId)
        }

        setData(refreshedData.sort(sortBillingEvents))
        setReversalStatus(refreshedReversalStatus)

        // Reset edit state - order matters for proper modal closure
        setIsEditing(false)
        setShowEditConfirmation(false)
        setEditMode(null)
        setEditFormData({
          eventType: '',
          eventSubType: '',
          postingDate: '',
          transactionDate: '',
          amount: 0,
          modifiedDescription: '',
        })
      } else {
        dispatch(
          pushToast({
            type: 'error',
            message: 'Failed to edit event',
            description: result.message,
          })
        )
        // Reset state even on API failure
        setShowEditConfirmation(false)
        setEditMode(null)
        setEditFormData({
          eventType: '',
          eventSubType: '',
          postingDate: '',
          transactionDate: '',
          amount: 0,
          modifiedDescription: '',
        })
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error occurred'
      dispatch(
        pushToast({
          type: 'error',
          message: 'Failed to edit event',
          description: errorMessage,
        })
      )
      // Reset state on exception
      setShowEditConfirmation(false)
      setEditMode(null)
      setEditFormData({
        eventType: '',
        eventSubType: '',
        postingDate: '',
        transactionDate: '',
        amount: 0,
        modifiedDescription: '',
      })
    } finally {
      // Always ensure these are reset, even on error
      setIsEditing(false)
      setShowEditConfirmation(false)
    }
  }

  // Toggle dropdown for specific row with smart positioning
  const toggleDropdown = (eventId: string, buttonElement: HTMLButtonElement) => {
    if (activeDropdownId === eventId) {
      setActiveDropdownId(null)
      return
    }

    // Calculate position relative to viewport
    const rect = buttonElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Dropdown dimensions - now only simple menu (no date picker)
    const dropdownHeight = 50 // Simple menu with options
    const dropdownWidth = 280

    // Calculate available space in all directions
    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top
    const spaceRight = viewportWidth - rect.left
    const spaceLeft = rect.left

    // Vertical positioning logic
    let finalY: number
    if (spaceBelow >= dropdownHeight + 20) {
      // Enough space below, open downward
      finalY = rect.bottom + 4
    } else if (spaceAbove >= dropdownHeight + 20) {
      // Not enough space below but enough above, open upward
      finalY = rect.top - dropdownHeight - 4
    } else {
      // Neither direction has enough space, choose the one with more space
      if (spaceBelow >= spaceAbove) {
        finalY = Math.max(viewportHeight - dropdownHeight - 20, rect.bottom + 4)
      } else {
        finalY = Math.max(20, rect.top - dropdownHeight - 4)
      }
    }

    // Horizontal positioning logic
    let finalX: number
    if (spaceRight >= dropdownWidth + 20) {
      finalX = rect.left
    } else if (spaceLeft >= dropdownWidth + 20) {
      finalX = rect.right - dropdownWidth
    } else {
      finalX = Math.max(20, Math.min(viewportWidth - dropdownWidth - 20, rect.left))
    }

    setDropdownCoords({
      x: finalX,
      y: finalY,
    })
    setActiveDropdownId(eventId)
  }

  // Toggle dropdown for audit log rows with smart positioning
  const toggleAuditLogDropdown = (auditLogCuid: string, buttonElement: HTMLButtonElement) => {
    if (activeAuditLogDropdownId === auditLogCuid) {
      setActiveAuditLogDropdownId(null)
      return
    }

    // Calculate position relative to viewport
    const rect = buttonElement.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    // Dropdown dimensions
    const dropdownHeight = 90 // Two menu items
    const dropdownWidth = 220

    // Calculate available space in all directions
    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top
    const spaceRight = viewportWidth - rect.left
    const spaceLeft = rect.left

    // Vertical positioning logic
    let finalY: number
    if (spaceBelow >= dropdownHeight + 20) {
      finalY = rect.bottom + 4
    } else if (spaceAbove >= dropdownHeight + 20) {
      finalY = rect.top - dropdownHeight - 4
    } else {
      if (spaceBelow >= spaceAbove) {
        finalY = Math.max(viewportHeight - dropdownHeight - 20, rect.bottom + 4)
      } else {
        finalY = Math.max(20, rect.top - dropdownHeight - 4)
      }
    }

    // Horizontal positioning logic
    let finalX: number
    if (spaceRight >= dropdownWidth + 20) {
      finalX = rect.left
    } else if (spaceLeft >= dropdownWidth + 20) {
      finalX = rect.right - dropdownWidth
    } else {
      finalX = Math.max(20, Math.min(viewportWidth - dropdownWidth - 20, rect.left))
    }

    setAuditLogDropdownCoords({
      x: finalX,
      y: finalY,
    })
    setActiveAuditLogDropdownId(auditLogCuid)
  }

  // Add click outside and scroll listeners for dropdown menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdownId) {
        const dropdownElement = dropdownRefs.current[activeDropdownId]
        if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
          setActiveDropdownId(null)
        }
      }
    }

    const handleScroll = () => {
      if (activeDropdownId) {
        setActiveDropdownId(null)
      }
    }

    if (activeDropdownId) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('scroll', handleScroll, true)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [activeDropdownId])

  // Add click outside and scroll listeners for audit log dropdown menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeAuditLogDropdownId) {
        const dropdownElement = auditLogDropdownRefs.current[activeAuditLogDropdownId]
        if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
          setActiveAuditLogDropdownId(null)
        }
      }
    }

    const handleScroll = () => {
      if (activeAuditLogDropdownId) {
        setActiveAuditLogDropdownId(null)
      }
    }

    if (activeAuditLogDropdownId) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('scroll', handleScroll, true)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [activeAuditLogDropdownId])

  // Event Type header with filter
  const renderEventTypeHeader = () => (
    <div className="relative inline-flex items-center gap-1" ref={dropdownRef}>
      <span>Event Type</span>
      <button
        onClick={() => setIsFilterOpen(!isFilterOpen)}
        className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
          selectedEventTypes.length > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'
        }`}
        title="Filter event types"
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {selectedEventTypes.length > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200">
          {selectedEventTypes.length}
        </span>
      )}

      {isFilterOpen && (
        <div
          className="absolute top-full left-0 z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg p-3"
          style={{ minWidth: '250px' }}
        >
          <Select<EventTypeOption, true>
            value={selectedEventTypes}
            options={eventTypeOptions}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            isMulti
            styles={selectStyles}
            placeholder="Select types..."
            onChange={(selected) => setSelectedEventTypes([...selected])}
            isClearable
            autoFocus
            maxMenuHeight={200}
            menuPortalTarget={typeof document === 'undefined' ? null : document.body}
            menuPosition="fixed"
          />
          {selectedEventTypes.length > 0 && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Showing {filteredData.length} of {data.length} events
            </div>
          )}
        </div>
      )}
    </div>
  )

  const chargesGroupCollapsed = collapsedColumnGroups.has(CHARGES_COLUMN_GROUP_ID)

  const renderChargesGroupLabel = (collapsed: boolean): React.ReactNode => (
    <button
      type="button"
      onClick={() => toggleColumnGroup(CHARGES_COLUMN_GROUP_ID)}
      className="inline-flex items-center gap-1 normal-case text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
    >
      <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>
      <span>Charges</span>
    </button>
  )

  const billingEventsColConfig = useMemo((): Array<TableColumnConfig<TenantAccountBillingEventEntry>> => {
    const showUnbilledPayableColumn = hasNonZeroNumericValue(
      data,
      (row) => row.unbilledPayableTenantToCorp
    )
    const showUnbilledBalanceColumn = hasNonZeroNumericValue(
      data,
      (row) => row.unbilledBalanceTenantToCorp
    )

    const expandedChargeColumns: Array<TableColumnConfig<TenantAccountBillingEventEntry>> = [
      {
        label: 'Gross KE Charge',
        groupLabel: 'Charges',
        renderGroupLabel: () => renderChargesGroupLabel(false),
        align: 'right',
        headerTitle: 'Gross KE Charge',
        render: (row) => formatBillingCurrency(row.grossKECharge),
      },
      {
        label: 'KE Discount',
        align: 'right',
        render: (row) => formatBillingCurrency(row.keDiscount),
      },
      {
        label: 'Utility (T→K)',
        align: 'right',
        headerTitle: 'Utility Charge (Tenant → Corp)',
        render: (row) => formatBillingCurrency(row.utilityChargeTenantToCorp),
      },
      {
        label: 'Utility (K→U)',
        align: 'right',
        headerTitle: 'Utility Charge (Corp → Utility)',
        render: (row) => formatBillingCurrency(row.utilityChargeCorpToUtility),
      },
      ...(showUnbilledPayableColumn
        ? [
            {
              label: 'Unbilled (T→K)',
              align: 'right' as const,
              headerTitle: 'Unbilled Payable (Tenant → Corp)',
              render: (row: TenantAccountBillingEventEntry) => formatBillingCurrency(row.unbilledPayableTenantToCorp),
            },
          ]
        : []),
      {
        label: 'Billed (T→K)',
        align: 'right',
        headerTitle: 'Billed Payable (Tenant → Corp)',
        render: (row) => formatBillingCurrency(row.billedPayableTenantToCorp),
      },
      {
        label: 'Due (T→K)',
        align: 'right',
        headerTitle: 'Due Payable (Tenant → Corp)',
        render: (row) => formatBillingCurrency(row.duePayableTenantToCorp),
      },
      {
        label: 'Billed (K→U)',
        align: 'right',
        headerTitle: 'Billed Payable (Corp → Utility)',
        render: (row) => formatBillingCurrency(row.billedPayableCorpToUtility),
      },
      {
        label: 'Due (K→U)',
        align: 'right',
        headerTitle: 'Due Payable (Corp → Utility)',
        dividerAfter: true,
        render: (row) => formatBillingCurrency(row.duePayableCorpToUtility),
      },
    ]

    const chargeColumns: Array<TableColumnConfig<TenantAccountBillingEventEntry>> = chargesGroupCollapsed
      ? [
          {
            label: '',
            groupLabel: 'Charges',
            renderGroupLabel: () => renderChargesGroupLabel(true),
            dividerAfter: true,
            headerClassName: 'w-12',
            cellClassName: 'w-12 text-center text-slate-400',
            render: () => '…',
          },
        ]
      : expandedChargeColumns

    return [
      {
        label: '',
        groupLabel: 'Event',
        isFrozen: true,
        frozenMinWidth: 36,
        contentClassName: 'w-7 flex justify-center',
        render: (row) => (
          <div className="relative w-7 flex justify-center">
            {(hasEditBillingEventPermission() || canReclass(row.eventType) || canReverse(row.eventType) || canReverseInvoice(row.eventType)) && (
              <div
                className="relative"
                ref={(el) => {
                  dropdownRefs.current[row.eventId] = el
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleDropdown(row.eventId, e.currentTarget)
                  }}
                  className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Actions"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ),
      },
      {
        label: 'Invoice #',
        isFrozen: true,
        frozenMinWidth: 68,
        cellClassName: 'overflow-visible',
        render: (row) => <TruncatedCellWithTooltip text={row.invoiceNumber || 'N/A'} maxWidthClass="max-w-[68px]" />,
      },
      {
        label: 'Event ID',
        isFrozen: true,
        frozenMinWidth: 52,
        cellClassName: 'overflow-visible',
        render: (row) => <TruncatedCellWithTooltip text={row.eventId} maxWidthClass="max-w-[52px]" />,
      },
      {
        isFrozen: true,
        frozenMinWidth: 180,
        cellClassName: 'overflow-visible',
        renderHeader: renderEventTypeHeader,
        render: (row) => <EnumBadge status={row.eventType} />,
      },
      {
        label: 'Event Subtype',
        isFrozen: true,
        frozenMinWidth: 240,
        cellClassName: 'overflow-visible',
        render: (row) => <EnumBadge status={row.eventSubtype} />,
      },
      {
        label: 'Entry Type',
        groupLabel: 'Event details',
        headerClassName: 'min-w-[88px]',
        cellClassName: 'min-w-[88px]',
        render: (row) => <EnumBadge status={row.entryType} />,
      },
      {
        label: 'Post Date',
        align: 'right',
        cellClassName: 'tabular-nums',
        render: (row) => formatBillingDate(row.postDate),
      },
      {
        label: 'Tx Date',
        align: 'right',
        cellClassName: 'tabular-nums',
        render: (row) => formatBillingDate(row.txDate),
      },
      {
        label: 'Amount',
        align: 'right',
        dividerAfter: true,
        render: (row) => formatBillingCurrency(row.amount),
      },
      ...chargeColumns,
      ...(showUnbilledBalanceColumn
        ? [
            {
              label: 'Unbilled (T→K)',
              groupLabel: 'Running totals',
              columnSection: 'running-balance' as const,
              align: 'right' as const,
              headerTitle: 'Unbilled Balance (Tenant → Corp)',
              render: (row: TenantAccountBillingEventEntry) => formatBillingCurrency(row.unbilledBalanceTenantToCorp),
            },
          ]
        : []),
      {
        label: 'Billed (T→K)',
        ...(!showUnbilledBalanceColumn ? { groupLabel: 'Running totals' } : {}),
        columnSection: 'running-balance',
        align: 'right',
        headerTitle: 'Billed Balance (Tenant → Corp)',
        render: (row) => formatBillingCurrency(row.billedBalanceTenantToCorp),
      },
      {
        label: 'Due (T→K)',
        columnSection: 'running-balance',
        align: 'right',
        headerTitle: 'Due Balance (Tenant → Corp)',
        render: (row) => formatBillingCurrency(row.dueBalanceTenantToCorp),
      },
      {
        label: 'Billed (K→U)',
        columnSection: 'running-balance',
        align: 'right',
        headerTitle: 'Billed Balance (Corp → Utility)',
        render: (row) => formatBillingCurrency(row.billedBalanceCorpToUtility),
      },
      {
        label: 'Due (K→U)',
        columnSection: 'running-balance',
        align: 'right',
        headerTitle: 'Due Balance (Corp → Utility)',
        render: (row) => formatBillingCurrency(row.dueBalanceCorpToUtility),
      },
      {
        label: 'Balance On Date',
        columnSection: 'running-balance',
        align: 'right',
        cellClassName: 'tabular-nums',
        dividerAfter: true,
        render: (row) => formatBillingDate(row.balanceOnDate),
      },
      {
        label: 'Has KE accrual?',
        groupLabel: 'Other',
        columnSection: 'ancillary',
        render: (row) => <Badge status={row.hasKEAccrual ? 'YES' : 'NO'} variant="soft" />,
      },
      {
        label: 'Lifetime Savings',
        columnSection: 'ancillary',
        align: 'right',
        render: (row) => formatBillingCurrency(row.lifetimeSavings),
      },
    ]
  }, [chargesGroupCollapsed, data, renderEventTypeHeader, permissions])

  const auditLogColConfig = useMemo((): Array<TableColumnConfig<BillingEventAuditLogEntry>> => {
    return [
      {
        label: '',
        isFrozen: true,
        frozenMinWidth: 44,
        contentClassName: 'w-7 flex justify-center',
        render: (row) => (
          <div className="relative w-7 flex justify-center">
            {hasEditBillingEventPermission() && (
              <div
                className="relative"
                ref={(el) => {
                  auditLogDropdownRefs.current[row.cuid] = el
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleAuditLogDropdown(row.cuid, e.currentTarget)
                  }}
                  className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="Actions"
                >
                  <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ),
      },
      {
        label: 'Created At',
        isFrozen: true,
        frozenMinWidth: 90,
        align: 'right',
        cellClassName: 'tabular-nums',
        render: (row) => formatBillingDate(row.createdAt),
      },
      {
        label: 'Status',
        isFrozen: true,
        frozenMinWidth: 92,
        render: (row) => <Badge status={row.status} variant="soft" />,
      },
      {
        label: 'Destination',
        columnWidth: 100,
        cellClassName: 'overflow-visible',
        render: (row) => <EnumBadge status={row.destination} className="max-w-full" />,
      },
      {
        label: 'Event Type',
        columnWidth: 220,
        cellClassName: 'overflow-visible',
        render: (row) => <EnumBadge status={row.eventBody.eventType} className="max-w-full" />,
      },
      {
        label: 'Event Subtype',
        columnWidth: 100,
        cellClassName: 'overflow-visible',
        render: (row) => <EnumBadge status={row.eventBody.eventSubType} className="max-w-full" />,
      },
      {
        label: 'Amount',
        columnWidth: 112,
        align: 'right',
        render: (row) =>
          formatBillingCurrency(row.eventBody.amount != null ? parseFloat(row.eventBody.amount) : null),
      },
      {
        label: 'Post Date',
        columnWidth: 90,
        align: 'right',
        cellClassName: 'tabular-nums',
        render: (row) => formatBillingDate(row.eventBody.postingDate),
      },
      {
        label: 'Tx Date',
        columnWidth: 90,
        align: 'right',
        cellClassName: 'tabular-nums',
        render: (row) => formatBillingDate(row.eventBody.transactionDate),
      },
      {
        label: 'Source Entity',
        columnMinWidth: 200,
        cellClassName: 'overflow-visible',
        render: (row) => (
          <TruncatedCellWithTooltip text={row.eventBody.sourceEntity || 'N/A'} maxWidthClass="w-full min-w-0" />
        ),
      },
    ]
  }, [permissions])

  useEffect(() => {
    const fetchBillingEvents = async () => {
      setLoading(true)
      setError(null)
      try {
        const result = await getBillingEventsApi(props.tenantAccountId)
        setData(result.sort(sortBillingEvents))

        if (hasTakePaymentPermission() || hasCreateBillingEventPermission()) {
          try {
            const reversalStatusResult = await getReversalStatusApi(props.tenantAccountId)
            setReversalStatus(reversalStatusResult)
          } catch (err) {
            console.error('Failed to fetch reversal status:', err)
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred')
        console.error('Error fetching billing events:', err)
      } finally {
        setLoading(false)
      }
    }

    if (props.tenantAccountId) {
      fetchBillingEvents()
    }
  }, [props.tenantAccountId])

  // Fetch enum values on component mount
  useEffect(() => {
    const fetchEnums = async () => {
      setEnumsLoading(true)
      try {
        const enums = await getBillingEventEnumsApi()
        // Sort the enum arrays alphabetically
        const sortedEnums = {
          eventTypes: [...enums.eventTypes].sort(),
          eventSubTypes: [...enums.eventSubTypes].sort(),
        }
        setBillingEventEnums(sortedEnums)
      } catch (err) {
        console.error('Error fetching billing event enums:', err)
        dispatch(
          pushToast({
            type: 'error',
            message: 'Failed to load dropdown options',
            description: 'Will use default values',
          })
        )
      } finally {
        setEnumsLoading(false)
      }
    }

    if (!billingEventEnums) {
      fetchEnums()
    }
  }, [])

  // Fetch audit log data
  useEffect(() => {
    const fetchAuditLog = async () => {
      setAuditLogLoading(true)
      setAuditLogError(null)
      try {
        const result = await getBillingEventAuditLogApi(props.tenantAccountId)
        setAuditLogData(result)
      } catch (err) {
        setAuditLogError(err instanceof Error ? err.message : 'Unknown error occurred')
        console.error('Error fetching billing event audit log:', err)
      } finally {
        setAuditLogLoading(false)
      }
    }

    if (props.tenantAccountId) {
      fetchAuditLog()
    }
  }, [props.tenantAccountId])

  if (loading) {
    return <div>Loading billing events...</div>
  }

  if (error) {
    return <div>Error loading billing events: {error}</div>
  }

  return (
    <>
      {data.length > 0 ? (
        <div>
          <Table<TenantAccountBillingEventEntry>
            compact
            striped
            stickyHeader
            showColumnGroups
            scrollShell={{
              expandable: true,
              collapsedHeightClassName: 'max-h-56',
              heightClassName: 'max-h-64 sm:max-h-80 md:max-h-96 lg:max-h-[45vh]',
            }}
            // Avoid opacity on the row/cells — it makes sticky frozen columns translucent
            // so scrolling body content shows through. Mute text instead.
            rowClassName={(row) =>
              hasBeenReversed(row.eventId) ? 'text-slate-400 dark:text-slate-500' : ''
            }
            colConfig={billingEventsColConfig}
            data={{
              results: filteredData,
              total: filteredData.length,
              page: 1,
              resultsPerPage: filteredData.length,
            }}
            hidePaginator
          />
        </div>
      ) : (
        <div>No billing events available for this service account</div>
      )}

      {/* Billing Event Audit Log Section */}
      <div className="mt-8">
        <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <span>Billing Event Audit Log</span>
          {auditLogData.length > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-200">
              {auditLogData.length}
            </span>
          )}
        </h3>

        {auditLogLoading ? (
          <div>Loading audit log...</div>
        ) : auditLogError ? (
          <div>Error loading audit log: {auditLogError}</div>
        ) : auditLogData.length > 0 ? (
          <Table<BillingEventAuditLogEntry>
            compact
            striped
            stickyHeader
            tableClassName="w-full table-fixed"
            scrollShell={{
              expandable: true,
              collapsedHeightClassName: 'max-h-56',
              heightClassName: 'max-h-64 sm:max-h-80 md:max-h-96 lg:max-h-[45vh]',
            }}
            colConfig={auditLogColConfig}
            data={{
              results: auditLogData,
              total: auditLogData.length,
              page: 1,
              resultsPerPage: auditLogData.length,
            }}
            hidePaginator
          />
        ) : (
          <div>No audit log entries found (QUEUED or SKIPPED) for this service account</div>
        )}
      </div>

      {/* Edit Modal */}
      {editMode &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Billing Event #{editMode}</h3>
                <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Event Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Event Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editFormData.eventType || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, eventType: e.target.value })}
                      className={getInputStyles()}
                      required
                      disabled={enumsLoading}
                    >
                      <option value="">Select event type...</option>
                      {billingEventEnums?.eventTypes?.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Event Subtype */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Event Subtype <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={editFormData.eventSubType || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, eventSubType: e.target.value })}
                      className={getInputStyles()}
                      required
                      disabled={enumsLoading}
                    >
                      <option value="">Select subtype...</option>
                      {billingEventEnums?.eventSubTypes?.map((subtype) => (
                        <option key={subtype} value={subtype}>
                          {subtype}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Posting Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Posting Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={editFormData.postingDate || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, postingDate: e.target.value })}
                      className={getInputStyles()}
                      required
                    />
                  </div>

                  {/* Transaction Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Transaction Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={editFormData.transactionDate || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, transactionDate: e.target.value })}
                      className={getInputStyles()}
                      required
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Amount ($) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.amount || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, amount: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                      required
                    />
                  </div>

                  {/* Corp Energy Charge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Gross KE Charge ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.corpEnergyCharge === 0 ? '' : editFormData.corpEnergyCharge || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, corpEnergyCharge: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Corp Discount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">KE Discount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.corpDiscount === 0 ? '' : editFormData.corpDiscount || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, corpDiscount: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Utility Charge Tenant to Corp */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Utility Charge (T→K) ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.utilityChargeTenantToCorp === 0 ? '' : editFormData.utilityChargeTenantToCorp || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, utilityChargeTenantToCorp: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Utility Charge Corp to Utility */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Utility Charge (K→U) ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.utilityChargeCorpToUtility === 0 ? '' : editFormData.utilityChargeCorpToUtility || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, utilityChargeCorpToUtility: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Unbilled Payable Tenant to Corp */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unbilled Payable (T→K) ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.unbilledPayableTenantToCorp === 0 ? '' : editFormData.unbilledPayableTenantToCorp || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, unbilledPayableTenantToCorp: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Payable Tenant to Corp */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Billed Payable (T→K) ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.payableTenantToCorp === 0 ? '' : editFormData.payableTenantToCorp || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, payableTenantToCorp: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Due Tenant to Corp */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Payable (T→K) ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.dueTenantToCorp === 0 ? '' : editFormData.dueTenantToCorp || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, dueTenantToCorp: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Payable Corp to Utility */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Billed Payable (K→U) ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.payableCorpToUtility === 0 ? '' : editFormData.payableCorpToUtility || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, payableCorpToUtility: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Due Corp to Utility */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Payable (K→U) ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.dueCorpToUtility === 0 ? '' : editFormData.dueCorpToUtility || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, dueCorpToUtility: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Corp NSF Charge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Corp NSF Charge ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.corpNsfCharge === 0 ? '' : editFormData.corpNsfCharge || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, corpNsfCharge: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Corp Late Fee Charge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Corp Late Fee Charge ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.corpLateFeeCharge === 0 ? '' : editFormData.corpLateFeeCharge || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, corpLateFeeCharge: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Corp Processing Charge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Corp Processing Charge ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.corpProcessingCharge === 0 ? '' : editFormData.corpProcessingCharge || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, corpProcessingCharge: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Misc Charge */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Misc Charge ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.miscCharge === 0 ? '' : editFormData.miscCharge || ''}
                      placeholder="0.00"
                      onChange={(e) => setEditFormData({ ...editFormData, miscCharge: parseFloat(e.target.value) || 0 })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Modified Description - Required */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description of Changes <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={editFormData.modifiedDescription}
                      onChange={(e) => setEditFormData({ ...editFormData, modifiedDescription: e.target.value })}
                      className={getInputStyles()}
                      rows={3}
                      placeholder="Required: Describe what is being changed and why..."
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <Button size={1} color="slate" onClick={cancelEdit} disabled={isEditing}>
                  Cancel
                </Button>
                <Button
                  size={1}
                  onClick={handleEditSubmit}
                  disabled={
                    isEditing ||
                    !editFormData.modifiedDescription.trim() ||
                    !editFormData.eventType ||
                    !editFormData.eventSubType ||
                    !editFormData.postingDate ||
                    !editFormData.transactionDate ||
                    editFormData.amount === undefined ||
                    editFormData.amount === null
                  }
                >
                  {isEditing ? 'Saving...' : 'Review Changes'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Edit Confirmation Modal */}
      {showEditConfirmation &&
        editMode &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Confirm Changes to Event #{editMode}</h3>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">The following changes will be applied directly to the billing event:</p>

                  {(() => {
                    const originalEvent = data.find((row) => row.eventId === editMode)
                    if (!originalEvent) return null

                    const changes: string[] = []

                    // BillingEvent field changes
                    if (editFormData.eventType !== originalEvent.eventType) {
                      changes.push(`Event Type: ${originalEvent.eventType} → ${editFormData.eventType}`)
                    }
                    if (editFormData.eventSubType !== originalEvent.eventSubtype) {
                      changes.push(`Event Subtype: ${originalEvent.eventSubtype} → ${editFormData.eventSubType}`)
                    }
                    if (editFormData.postingDate && editFormData.postingDate !== new Date(originalEvent.postDate).toISOString().split('T')[0]) {
                      changes.push(`Posting Date: ${formatUTCDate(new Date(originalEvent.postDate))} → ${formatUTCDate(new Date(editFormData.postingDate))}`)
                    }
                    if (editFormData.transactionDate && editFormData.transactionDate !== new Date(originalEvent.txDate).toISOString().split('T')[0]) {
                      changes.push(
                        `Transaction Date: ${formatUTCDate(new Date(originalEvent.txDate))} → ${formatUTCDate(new Date(editFormData.transactionDate))}`
                      )
                    }
                    if (editFormData.amount !== undefined && editFormData.amount !== originalEvent.amount) {
                      changes.push(`Amount: ${formatCurrency(originalEvent.amount)} → ${formatCurrency(editFormData.amount)}`)
                    }

                    // BillingEntry field changes
                    if (editFormData.corpEnergyCharge !== undefined && editFormData.corpEnergyCharge !== originalEvent.grossKECharge) {
                      changes.push(`Gross KE Charge: ${formatCurrency(originalEvent.grossKECharge)} → ${formatCurrency(editFormData.corpEnergyCharge)}`)
                    }
                    if (editFormData.corpDiscount !== undefined && editFormData.corpDiscount !== originalEvent.keDiscount) {
                      changes.push(`KE Discount: ${formatCurrency(originalEvent.keDiscount)} → ${formatCurrency(editFormData.corpDiscount)}`)
                    }
                    if (
                      editFormData.utilityChargeTenantToCorp !== undefined &&
                      editFormData.utilityChargeTenantToCorp !== originalEvent.utilityChargeTenantToCorp
                    ) {
                      changes.push(
                        `Utility Charge (T→K): ${formatCurrency(originalEvent.utilityChargeTenantToCorp)} → ${formatCurrency(
                          editFormData.utilityChargeTenantToCorp
                        )}`
                      )
                    }
                    if (
                      editFormData.utilityChargeCorpToUtility !== undefined &&
                      editFormData.utilityChargeCorpToUtility !== originalEvent.utilityChargeCorpToUtility
                    ) {
                      changes.push(
                        `Utility Charge (K→U): ${formatCurrency(originalEvent.utilityChargeCorpToUtility)} → ${formatCurrency(
                          editFormData.utilityChargeCorpToUtility
                        )}`
                      )
                    }
                    if (
                      editFormData.unbilledPayableTenantToCorp !== undefined &&
                      editFormData.unbilledPayableTenantToCorp !== originalEvent.unbilledPayableTenantToCorp
                    ) {
                      changes.push(
                        `Unbilled Payable (T→K): ${formatCurrency(originalEvent.unbilledPayableTenantToCorp)} → ${formatCurrency(
                          editFormData.unbilledPayableTenantToCorp
                        )}`
                      )
                    }
                    if (editFormData.payableTenantToCorp !== undefined && editFormData.payableTenantToCorp !== originalEvent.billedPayableTenantToCorp) {
                      changes.push(
                        `Billed Payable (T→K): ${formatCurrency(originalEvent.billedPayableTenantToCorp)} → ${formatCurrency(editFormData.payableTenantToCorp)}`
                      )
                    }
                    if (editFormData.dueTenantToCorp !== undefined && editFormData.dueTenantToCorp !== originalEvent.duePayableTenantToCorp) {
                      changes.push(
                        `Due Payable (T→K): ${formatCurrency(originalEvent.duePayableTenantToCorp)} → ${formatCurrency(editFormData.dueTenantToCorp)}`
                      )
                    }
                    if (editFormData.payableCorpToUtility !== undefined && editFormData.payableCorpToUtility !== originalEvent.billedPayableCorpToUtility) {
                      changes.push(
                        `Billed Payable (K→U): ${formatCurrency(originalEvent.billedPayableCorpToUtility)} → ${formatCurrency(
                          editFormData.payableCorpToUtility
                        )}`
                      )
                    }
                    if (editFormData.dueCorpToUtility !== undefined && editFormData.dueCorpToUtility !== originalEvent.duePayableCorpToUtility) {
                      changes.push(
                        `Due Payable (K→U): ${formatCurrency(originalEvent.duePayableCorpToUtility)} → ${formatCurrency(editFormData.dueCorpToUtility)}`
                      )
                    }

                    // Additional BillingEntry fields
                    if (editFormData.corpNsfCharge !== undefined && editFormData.corpNsfCharge !== originalEvent.corpNsfCharge) {
                      changes.push(`Corp NSF Charge: ${formatCurrency(originalEvent.corpNsfCharge)} → ${formatCurrency(editFormData.corpNsfCharge)}`)
                    }
                    if (editFormData.corpLateFeeCharge !== undefined && editFormData.corpLateFeeCharge !== originalEvent.corpLateFeeCharge) {
                      changes.push(
                        `Corp Late Fee Charge: ${formatCurrency(originalEvent.corpLateFeeCharge)} → ${formatCurrency(editFormData.corpLateFeeCharge)}`
                      )
                    }
                    if (editFormData.corpProcessingCharge !== undefined && editFormData.corpProcessingCharge !== originalEvent.corpProcessingCharge) {
                      changes.push(
                        `Corp Processing Charge: ${formatCurrency(originalEvent.corpProcessingCharge)} → ${formatCurrency(editFormData.corpProcessingCharge)}`
                      )
                    }
                    if (editFormData.miscCharge !== undefined && editFormData.miscCharge !== originalEvent.miscCharge) {
                      changes.push(`Misc Charge: ${formatCurrency(originalEvent.miscCharge)} → ${formatCurrency(editFormData.miscCharge)}`)
                    }

                    if (changes.length === 0) {
                      return <p className="text-sm text-gray-500 italic">No changes detected</p>
                    }

                    return (
                      <ul className="text-sm space-y-1">
                        {changes.map((change, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-blue-500 mr-2">•</span>
                            <span>{change}</span>
                          </li>
                        ))}
                      </ul>
                    )
                  })()}
                </div>

                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Note:</strong> This will directly update the billing event. Derived balance calculations will be updated automatically by the
                    system.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason for changes:</label>
                  <p className="text-sm bg-gray-50 dark:bg-gray-700 p-2 rounded border">{editFormData.modifiedDescription}</p>
                </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <Button size={1} color="slate" onClick={() => setShowEditConfirmation(false)} disabled={isEditing}>
                  Back to Edit
                </Button>
                <Button size={1} onClick={confirmEdit} disabled={isEditing}>
                  {isEditing ? (
                    <>
                      <svg
                        className="mr-2 h-4 w-4 inline-block"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        style={{
                          animation: 'spin 1s linear infinite',
                        }}
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Confirming...
                    </>
                  ) : (
                    'Confirm Changes'
                  )}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Fixed positioned dropdown */}
      {activeDropdownId && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1 min-w-[280px]"
          style={{
            left: dropdownCoords.x,
            top: dropdownCoords.y,
          }}
          ref={(el) => {
            dropdownRefs.current[activeDropdownId] = el
          }}
        >
          {(() => {
            const row = data.find((r) => r.eventId === activeDropdownId)
            if (!row) return null

            const isReversed = hasBeenReversed(row.eventId)
            const canEdit = hasEditBillingEventPermission()
            const canReclassThisEvent = canReclass(row.eventType)

            return (
              <div className="py-1">
                {/* Edit Event option */}
                {canEdit && (
                  <button
                    onClick={() => {
                      startEdit(row)
                      setActiveDropdownId(null)
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    Edit Event
                  </button>
                )}

                {/* Reclass option */}
                {canReclassThisEvent && (
                  <button
                    onClick={() => {
                      if (!isReversed) {
                        setSelectedEventForModal(row)
                        setIsReclassModalOpen(true)
                        setActiveDropdownId(null)
                      }
                    }}
                    disabled={isReversed}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                      isReversed ? 'text-gray-400 cursor-not-allowed line-through' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={isReversed ? 'This event has already been reclassified' : undefined}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Reclass as {getReclassTarget(row.eventType)}
                    {isReversed && <span className="ml-2 text-xs">(Already reclassified)</span>}
                  </button>
                )}

                {/* Reverse option */}
                {canReverse(row.eventType) && (
                  <button
                    onClick={() => {
                      if (!isReversed) {
                        setSelectedEventForModal(row)
                        setIsReverseModalOpen(true)
                        setActiveDropdownId(null)
                      }
                    }}
                    disabled={isReversed}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                      isReversed ? 'text-gray-400 cursor-not-allowed line-through' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={isReversed ? 'This event has already been reversed' : undefined}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    Reverse Payment
                    {isReversed && <span className="ml-2 text-xs">(Already reversed)</span>}
                  </button>
                )}

                {/* Reverse Invoice option */}
                {canReverseInvoice(row.eventType) && (
                  <button
                    onClick={() => {
                      if (!isReversed && !isReversingInvoice) {
                        setSelectedEventForModal(row)
                        setReverseInvoiceFormData({
                          amount: undefined,
                          eventSubType: row.eventSubtype || undefined,
                          modifiedDescription: '',
                        })
                        setReverseInvoiceUseOriginalAmount(true)
                        setReverseInvoiceAmountError(null)
                        setReverseInvoiceStep('form')
                        setIsReverseInvoiceModalOpen(true)
                        setActiveDropdownId(null)
                      }
                    }}
                    disabled={isReversed || isReversingInvoice}
                    className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                      isReversed
                        ? 'text-gray-400 cursor-not-allowed line-through'
                        : isReversingInvoice
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title={isReversed ? 'This event has already been reversed' : isReversingInvoice ? 'A reversal is already in progress' : undefined}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                    </svg>
                    Reverse Invoice
                    {isReversed && <span className="ml-2 text-xs">(Already reversed)</span>}
                  </button>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {/* Audit Log Dropdown Menu */}
      {activeAuditLogDropdownId && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg py-1 min-w-[220px]"
          style={{
            left: auditLogDropdownCoords.x,
            top: auditLogDropdownCoords.y,
          }}
          ref={(el) => {
            auditLogDropdownRefs.current[activeAuditLogDropdownId] = el
          }}
        >
          {(() => {
            const row = auditLogData.find((r) => r.cuid === activeAuditLogDropdownId)
            if (!row) return null

            return (
              <div className="py-1">
                {/* Change Status option */}
                <button
                  onClick={() => {
                    setSelectedAuditLog(row)
                    setAuditLogStatusForm({
                      status: row.status === 'QUEUED' ? 'SKIPPED' : 'QUEUED',
                      modifiedDescription: '',
                    })
                    setIsStatusModalOpen(true)
                    setActiveAuditLogDropdownId(null)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Change Status
                </button>

                {/* Edit Event Body option */}
                <button
                  onClick={() => {
                    setSelectedAuditLog(row)
                    setSelectedAuditLogTenantCuid(row.eventBody.tenantAccountCuid || '')
                    setAuditLogEventBodyForm({
                      eventType: row.eventBody.eventType || '',
                      transactionDate: row.eventBody.transactionDate ? row.eventBody.transactionDate.split('T')[0] : '',
                      sourceEntity: row.eventBody.sourceEntity || '',
                      amount: row.eventBody.amount ? parseFloat(row.eventBody.amount) : undefined,
                      skipChecks: row.eventBody.skipChecks || false,
                      eventSubType: row.eventBody.eventSubType || '',
                      transactionStartDate: row.eventBody.transactionStartDate ? row.eventBody.transactionStartDate.split('T')[0] : '',
                      modifiedDescription: '',
                    })
                    setIsEventBodyModalOpen(true)
                    setActiveAuditLogDropdownId(null)
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Edit Event Body
                </button>
              </div>
            )
          })()}
        </div>
      )}

      {/* Reclass Modal */}
      <CustomContentModal
        isOpen={isReclassModalOpen}
        onClose={() => {
          setIsReclassModalOpen(false)
          setSelectedEventForModal(null)
          setUseOriginalDate(true)
          setCustomTransactionDate('')
        }}
      >
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Reclass as {selectedEventForModal && getReclassTarget(selectedEventForModal.eventType)}
            </h3>
            <button
              onClick={() => {
                setIsReclassModalOpen(false)
                setSelectedEventForModal(null)
                setUseOriginalDate(true)
                setCustomTransactionDate('')
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="px-6 py-4">
            {selectedEventForModal && (
              <>
                <div className="mb-4">
                  <div className="text-xs text-gray-600 dark:text-gray-400">Original: {formatUTCDate(new Date(selectedEventForModal.txDate))}</div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Transaction Date</label>
                  <div className="space-y-2">
                    <label className="flex items-center text-sm">
                      <input type="radio" checked={useOriginalDate} onChange={() => setUseOriginalDate(true)} className="mr-2 text-indigo-600" />
                      Use original date
                    </label>
                    <label className="flex items-center text-sm">
                      <input type="radio" checked={!useOriginalDate} onChange={() => setUseOriginalDate(false)} className="mr-2 text-indigo-600" />
                      Custom date
                    </label>
                  </div>
                </div>

                {!useOriginalDate && (
                  <div className="mb-4">
                    <input
                      type="date"
                      value={customTransactionDate || new Date(selectedEventForModal.txDate).toISOString().split('T')[0]}
                      onChange={(e) => setCustomTransactionDate(e.target.value)}
                      className={getInputStyles()}
                    />
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    size={1}
                    disabled={isReclassifying}
                    onClick={() => {
                      const dateToUse = useOriginalDate
                        ? undefined
                        : customTransactionDate || new Date(selectedEventForModal.txDate).toISOString().split('T')[0]
                      const target = getReclassTarget(selectedEventForModal.eventType)
                      if (target) {
                        handleReclass(selectedEventForModal.eventId, selectedEventForModal.eventType, target, dateToUse)
                      }
                      setIsReclassModalOpen(false)
                      setSelectedEventForModal(null)
                      setUseOriginalDate(true)
                      setCustomTransactionDate('')
                    }}
                  >
                    {isReclassifying ? (
                      <>
                        <svg
                          className="mr-2 h-5 w-5 inline-block"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          style={{
                            animation: 'spin 1s linear infinite',
                          }}
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Confirm
                      </>
                    ) : (
                      'Confirm'
                    )}
                  </Button>
                  <Button
                    size={1}
                    color="slate"
                    disabled={isReclassifying}
                    onClick={() => {
                      setIsReclassModalOpen(false)
                      setSelectedEventForModal(null)
                      setUseOriginalDate(true)
                      setCustomTransactionDate('')
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </CustomContentModal>

      {/* Reverse Modal */}
      <CustomContentModal
        isOpen={isReverseModalOpen}
        onClose={() => {
          setIsReverseModalOpen(false)
          setSelectedEventForModal(null)
          setUseOriginalDate(true)
          setCustomTransactionDate('')
        }}
      >
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Reverse Payment</h3>
            <button
              onClick={() => {
                setIsReverseModalOpen(false)
                setSelectedEventForModal(null)
                setUseOriginalDate(true)
                setCustomTransactionDate('')
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="px-6 py-4">
            {selectedEventForModal && (
              <>
                <div className="mb-4">
                  <div className="text-xs text-gray-600 dark:text-gray-400">Original: {formatUTCDate(new Date(selectedEventForModal.txDate))}</div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Transaction Date</label>
                  <div className="space-y-2">
                    <label className="flex items-center text-sm">
                      <input type="radio" checked={useOriginalDate} onChange={() => setUseOriginalDate(true)} className="mr-2 text-indigo-600" />
                      Use original date
                    </label>
                    <label className="flex items-center text-sm">
                      <input type="radio" checked={!useOriginalDate} onChange={() => setUseOriginalDate(false)} className="mr-2 text-indigo-600" />
                      Custom date
                    </label>
                  </div>
                </div>

                {!useOriginalDate && (
                  <div className="mb-4">
                    <input
                      type="date"
                      value={customTransactionDate || new Date(selectedEventForModal.txDate).toISOString().split('T')[0]}
                      onChange={(e) => setCustomTransactionDate(e.target.value)}
                      className={getInputStyles()}
                    />
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    size={1}
                    disabled={isReclassifying}
                    onClick={() => {
                      const dateToUse = useOriginalDate
                        ? undefined
                        : customTransactionDate || new Date(selectedEventForModal.txDate).toISOString().split('T')[0]
                      handleReverse(selectedEventForModal.eventId, dateToUse)
                      setIsReverseModalOpen(false)
                      setSelectedEventForModal(null)
                      setUseOriginalDate(true)
                      setCustomTransactionDate('')
                    }}
                  >
                    {isReclassifying ? (
                      <>
                        <svg
                          className="mr-2 h-5 w-5 inline-block"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          style={{
                            animation: 'spin 1s linear infinite',
                          }}
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Confirm
                      </>
                    ) : (
                      'Confirm'
                    )}
                  </Button>
                  <Button
                    size={1}
                    color="slate"
                    disabled={isReclassifying}
                    onClick={() => {
                      setIsReverseModalOpen(false)
                      setSelectedEventForModal(null)
                      setUseOriginalDate(true)
                      setCustomTransactionDate('')
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </CustomContentModal>

      {/* Reverse Invoice Modal */}
      <CustomContentModal
        isOpen={isReverseInvoiceModalOpen}
        onClose={() => {
          if (!isReversingInvoice) {
            closeReverseInvoiceModal()
          }
        }}
      >
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {reverseInvoiceStep === 'confirm' ? 'Confirm Reverse Invoice' : 'Reverse Invoice'}
            </h3>
            <button
              onClick={() => {
                if (!isReversingInvoice) {
                  closeReverseInvoiceModal()
                }
              }}
              disabled={isReversingInvoice}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="px-6 py-4">
            {selectedEventForModal && reverseInvoiceStep === 'form' && (
              <>
                <div className="mb-4">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Original amount: {formatCurrency(selectedEventForModal.amount)} &nbsp;|&nbsp; Original date:{' '}
                    {formatUTCDate(new Date(selectedEventForModal.txDate))} (used as the reversal transaction date; not editable)
                  </div>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Amount</label>
                  <div className="space-y-2">
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        checked={reverseInvoiceUseOriginalAmount}
                        onChange={() => {
                          setReverseInvoiceUseOriginalAmount(true)
                          setReverseInvoiceAmountError(null)
                        }}
                        className="mr-2 text-indigo-600"
                      />
                      Original amount ({formatCurrency(selectedEventForModal.amount)})
                    </label>
                    <label
                      className={`flex items-center text-sm ${selectedEventForModal.amount === 0 ? 'text-gray-400 cursor-not-allowed' : ''}`}
                      title={selectedEventForModal.amount === 0 ? 'Zero-amount accruals can only be fully reversed' : undefined}
                    >
                      <input
                        type="radio"
                        checked={!reverseInvoiceUseOriginalAmount}
                        disabled={selectedEventForModal.amount === 0}
                        onChange={() => {
                          setReverseInvoiceUseOriginalAmount(false)
                          setReverseInvoiceAmountError(validateReverseInvoiceAmount(selectedEventForModal, reverseInvoiceFormData.amount))
                        }}
                        className="mr-2 text-indigo-600 disabled:opacity-50"
                      />
                      Custom amount
                    </label>
                  </div>
                  {!reverseInvoiceUseOriginalAmount && (
                    <div className="mt-2">
                      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                        Optional reversal amount. Leave blank for a full reversal. Cannot exceed the original invoice amount.
                      </p>
                      <div className="mb-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5 space-y-2">
                        <p>
                          In most circumstances, full reversals are preferred. Partial reversals, as with all reversals of INVOICE_ACCRUED events, only affect
                          the {'T->K'} balance, never the {'K->U'} balance.
                        </p>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={reverseInvoiceFormData.amount ?? ''}
                        onChange={(e) => {
                          const nextAmount = e.target.value !== '' ? parseFloat(e.target.value) : undefined
                          setReverseInvoiceFormData((prev) => ({ ...prev, amount: nextAmount }))
                          setReverseInvoiceAmountError(validateReverseInvoiceAmount(selectedEventForModal, nextAmount))
                        }}
                        className={getInputStyles()}
                        placeholder="Enter amount"
                      />
                      {reverseInvoiceAmountError && <p className="mt-1 text-xs text-red-500">{reverseInvoiceAmountError}</p>}
                    </div>
                  )}
                </div>

                {/* Event Subtype */}
                {billingEventEnums && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Event Subtype (optional)</label>
                    <select
                      value={reverseInvoiceFormData.eventSubType ?? ''}
                      onChange={(e) => setReverseInvoiceFormData((prev) => ({ ...prev, eventSubType: e.target.value || undefined }))}
                      className={getInputStyles()}
                    >
                      <option value="">— None —</option>
                      {billingEventEnums.eventSubTypes.map((subType) => (
                        <option key={subType} value={subType}>
                          {subType}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Reason for reversal */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reason for Reversal <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reverseInvoiceFormData.modifiedDescription}
                    onChange={(e) => setReverseInvoiceFormData((prev) => ({ ...prev, modifiedDescription: e.target.value }))}
                    className={getInputStyles()}
                    rows={3}
                    placeholder="Describe why this invoice is being reversed..."
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    size={1}
                    disabled={
                      !reverseInvoiceFormData.modifiedDescription.trim() ||
                      (!reverseInvoiceUseOriginalAmount && validateReverseInvoiceAmount(selectedEventForModal, reverseInvoiceFormData.amount) !== null)
                    }
                    onClick={() => setReverseInvoiceStep('confirm')}
                  >
                    Review
                  </Button>
                  <Button size={1} color="slate" onClick={closeReverseInvoiceModal}>
                    Cancel
                  </Button>
                </div>
              </>
            )}

            {selectedEventForModal && reverseInvoiceStep === 'confirm' && (
              <>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  This will queue an <span className="font-medium">INVOICE_REVERSED</span> event with the following values:
                </p>
                <dl className="mb-4 space-y-2 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500 dark:text-gray-400">Transaction Date</dt>
                    <dd className="text-gray-900 dark:text-gray-100">{formatUTCDate(new Date(selectedEventForModal.txDate))} (original accrual date)</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500 dark:text-gray-400">Amount</dt>
                    <dd className="text-gray-900 dark:text-gray-100">
                      {formatCurrency(reverseInvoiceUseOriginalAmount ? -selectedEventForModal.amount : -(reverseInvoiceFormData.amount ?? 0))}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-500 dark:text-gray-400">Event Subtype</dt>
                    <dd className="text-gray-900 dark:text-gray-100">{reverseInvoiceFormData.eventSubType || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400 mb-1">Reason</dt>
                    <dd className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{reverseInvoiceFormData.modifiedDescription}</dd>
                  </div>
                </dl>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    size={1}
                    isSaving={isReversingInvoice}
                    disabled={isReversingInvoice}
                    onClick={() => {
                      handleReverseInvoice(selectedEventForModal.eventId, buildReverseInvoicePayload())
                    }}
                  >
                    Confirm
                  </Button>
                  <Button size={1} color="slate" disabled={isReversingInvoice} onClick={() => setReverseInvoiceStep('form')}>
                    Back
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </CustomContentModal>

      {/* Audit Log Status Change Modal */}
      <CustomContentModal
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false)
          setSelectedAuditLog(null)
          setAuditLogStatusForm({ status: 'QUEUED', modifiedDescription: '' })
        }}
      >
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Change Audit Log Status</h3>
            <button
              onClick={() => {
                setIsStatusModalOpen(false)
                setSelectedAuditLog(null)
                setAuditLogStatusForm({ status: 'QUEUED', modifiedDescription: '' })
              }}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {selectedAuditLog && (
            <>
              <div className="px-6 py-4 space-y-4">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Current Status: <span className="font-semibold">{selectedAuditLog.status}</span>
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    New Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={auditLogStatusForm.status}
                    onChange={(e) => setAuditLogStatusForm({ ...auditLogStatusForm, status: e.target.value as 'QUEUED' | 'SKIPPED' })}
                    className={getInputStyles()}
                  >
                    <option value="QUEUED">QUEUED</option>
                    <option value="SKIPPED">SKIPPED</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Reason for Change <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={auditLogStatusForm.modifiedDescription}
                    onChange={(e) => setAuditLogStatusForm({ ...auditLogStatusForm, modifiedDescription: e.target.value })}
                    className={getInputStyles()}
                    rows={3}
                    placeholder="Describe why this status is being changed..."
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    size={1}
                    disabled={isUpdatingAuditLog || !auditLogStatusForm.modifiedDescription.trim()}
                    onClick={async () => {
                      setIsUpdatingAuditLog(true)
                      try {
                        await updateAuditLogStatusApi(props.tenantAccountId, selectedAuditLog.cuid, auditLogStatusForm)
                        dispatch(
                          pushToast({
                            type: 'success',
                            message: `Audit log status updated to ${auditLogStatusForm.status}`,
                          })
                        )
                        setIsStatusModalOpen(false)
                        setSelectedAuditLog(null)
                        setAuditLogStatusForm({ status: 'QUEUED', modifiedDescription: '' })
                        // Refresh audit log data
                        const updatedAuditLog = await getBillingEventAuditLogApi(props.tenantAccountId)
                        setAuditLogData(updatedAuditLog)
                      } catch (error: any) {
                        dispatch(
                          pushToast({
                            type: 'error',
                            message: error.response?.data?.message || 'Failed to update audit log status',
                          })
                        )
                      } finally {
                        setIsUpdatingAuditLog(false)
                      }
                    }}
                  >
                    {isUpdatingAuditLog ? 'Updating...' : 'Update Status'}
                  </Button>
                  <Button
                    size={1}
                    color="slate"
                    onClick={() => {
                      setIsStatusModalOpen(false)
                      setSelectedAuditLog(null)
                      setAuditLogStatusForm({ status: 'QUEUED', modifiedDescription: '' })
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </CustomContentModal>

      {/* Audit Log Edit Event Body Modal */}
      <CustomContentModal
        isOpen={isEventBodyModalOpen}
        onClose={() => {
          setIsEventBodyModalOpen(false)
          setSelectedAuditLog(null)
        }}
      >
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-3xl">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Edit Audit Log Event Body</h3>
            <button
              onClick={() => {
                setIsEventBodyModalOpen(false)
                setSelectedAuditLog(null)
              }}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {selectedAuditLog && (
            <>
              <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Tenant Account CUID (Read-only, from URL path) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tenant Account CUID</label>
                    <input
                      type="text"
                      value={selectedAuditLogTenantCuid}
                      className={`${getInputStyles()} bg-gray-100 dark:bg-gray-800 cursor-not-allowed`}
                      readOnly
                      disabled
                    />
                  </div>

                  {/* Event Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Event Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={auditLogEventBodyForm.eventType}
                      onChange={(e) => setAuditLogEventBodyForm({ ...auditLogEventBodyForm, eventType: e.target.value })}
                      className={getInputStyles()}
                      required
                      disabled={enumsLoading}
                    >
                      <option value="">Select event type...</option>
                      {billingEventEnums?.eventTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Event SubType */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Event SubType</label>
                    <select
                      value={auditLogEventBodyForm.eventSubType || ''}
                      onChange={(e) => setAuditLogEventBodyForm({ ...auditLogEventBodyForm, eventSubType: e.target.value || undefined })}
                      className={getInputStyles()}
                      disabled={enumsLoading}
                    >
                      <option value="">None</option>
                      {billingEventEnums?.eventSubTypes.map((subType) => (
                        <option key={subType} value={subType}>
                          {subType}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Transaction Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Transaction Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={auditLogEventBodyForm.transactionDate}
                      onChange={(e) => setAuditLogEventBodyForm({ ...auditLogEventBodyForm, transactionDate: e.target.value })}
                      className={getInputStyles()}
                      required
                    />
                  </div>

                  {/* Transaction Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Transaction Start Date</label>
                    <input
                      type="date"
                      value={auditLogEventBodyForm.transactionStartDate || ''}
                      onChange={(e) => setAuditLogEventBodyForm({ ...auditLogEventBodyForm, transactionStartDate: e.target.value || undefined })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Source Entity */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Entity</label>
                    <input
                      type="text"
                      value={auditLogEventBodyForm.sourceEntity || ''}
                      onChange={(e) => setAuditLogEventBodyForm({ ...auditLogEventBodyForm, sourceEntity: e.target.value || undefined })}
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={auditLogEventBodyForm.amount ?? ''}
                      onChange={(e) =>
                        setAuditLogEventBodyForm({
                          ...auditLogEventBodyForm,
                          amount: e.target.value ? parseFloat(e.target.value) : undefined,
                        })
                      }
                      className={getInputStyles()}
                    />
                  </div>

                  {/* Skip Checks */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="skipChecks"
                      checked={auditLogEventBodyForm.skipChecks || false}
                      onChange={(e) => setAuditLogEventBodyForm({ ...auditLogEventBodyForm, skipChecks: e.target.checked })}
                      className="h-4 w-4 text-indigo-600 rounded mr-2"
                    />
                    <label htmlFor="skipChecks" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Skip Checks
                    </label>
                  </div>
                </div>

                {/* Modification Description */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reason for Change <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={auditLogEventBodyForm.modifiedDescription}
                    onChange={(e) => setAuditLogEventBodyForm({ ...auditLogEventBodyForm, modifiedDescription: e.target.value })}
                    className={getInputStyles()}
                    rows={3}
                    placeholder="Describe what was changed and why..."
                    required
                  />
                </div>

                {/* Raw JSON Preview */}
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Raw JSON Preview</label>
                  <pre className="text-xs font-mono text-gray-800 dark:text-gray-200 text-left overflow-x-auto bg-white dark:bg-gray-900 p-3 rounded border border-gray-200 dark:border-gray-600">
                    {JSON.stringify(
                      {
                        tenantAccountCuid: selectedAuditLogTenantCuid,
                        eventType: auditLogEventBodyForm.eventType,
                        ...(auditLogEventBodyForm.eventSubType && { eventSubType: auditLogEventBodyForm.eventSubType }),
                        transactionDate: auditLogEventBodyForm.transactionDate,
                        ...(auditLogEventBodyForm.transactionStartDate && { transactionStartDate: auditLogEventBodyForm.transactionStartDate }),
                        ...(auditLogEventBodyForm.sourceEntity && { sourceEntity: auditLogEventBodyForm.sourceEntity }),
                        ...(auditLogEventBodyForm.amount !== undefined && auditLogEventBodyForm.amount !== null && { amount: auditLogEventBodyForm.amount }),
                        ...(auditLogEventBodyForm.skipChecks !== undefined && { skipChecks: auditLogEventBodyForm.skipChecks }),
                      },
                      null,
                      2
                    )}
                  </pre>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                  <Button
                    size={1}
                    disabled={
                      isUpdatingAuditLog ||
                      !auditLogEventBodyForm.eventType.trim() ||
                      !auditLogEventBodyForm.transactionDate.trim() ||
                      !auditLogEventBodyForm.modifiedDescription.trim()
                    }
                    onClick={async () => {
                      setIsUpdatingAuditLog(true)
                      try {
                        await updateAuditLogEventBodyApi(props.tenantAccountId, selectedAuditLog.cuid, auditLogEventBodyForm)
                        dispatch(
                          pushToast({
                            type: 'success',
                            message: 'Audit log event body updated successfully',
                          })
                        )
                        setIsEventBodyModalOpen(false)
                        setSelectedAuditLog(null)
                        // Refresh audit log data
                        const updatedAuditLog = await getBillingEventAuditLogApi(props.tenantAccountId)
                        setAuditLogData(updatedAuditLog)
                      } catch (error: any) {
                        dispatch(
                          pushToast({
                            type: 'error',
                            message: error.response?.data?.message || 'Failed to update audit log event body',
                          })
                        )
                      } finally {
                        setIsUpdatingAuditLog(false)
                      }
                    }}
                  >
                    {isUpdatingAuditLog ? 'Updating...' : 'Update Event Body'}
                  </Button>
                  <Button
                    size={1}
                    color="slate"
                    onClick={() => {
                      setIsEventBodyModalOpen(false)
                      setSelectedAuditLog(null)
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </CustomContentModal>
    </>
  )
}

export default TabBillingEvents
